import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { ContactFiltersApi } from 'entities/Contacts/api/contactsFilters'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import type { ChatbotTrigger, IChatbotFilters } from 'entities/Chatbot'
import type { IChatbotFullData } from 'entities/Chatbot/api/chatbotTypes'
import type { IParamsSendFrom } from 'entities/SendFrom'
import {
  EnrollmentFilters,
  EnrollmentStore,
  initEnrollmentFilters,
} from 'widgets/ContactEnrollment'
import { SendFromStore } from 'widgets/SendFrom'
import { SearchTypesEnum } from 'widgets/SearchDropdown'
import type { IEnrollmentActions } from 'widgets/ContactEnrollment/store/types'
import { EnrollByWebhookStore } from 'widgets/EnrollByWebhook/store/EnrollByWebhookStore.ts'

export class TriggerSectionStore {
  runAutomatically = false
  readonly enrollByWebhookStore: EnrollByWebhookStore
  sendFromError = ''
  enrollmentError = ''
  enrollmentStore: EnrollmentStore
  readonly sendFromStore: SendFromStore
  enrollmentFilters = EnrollmentFilters.Empty()
  enrollmentExcludeFilters = EnrollmentFilters.Empty()
  private _disposeIsShowContactsAlert: IReactionDisposer | null = null

  constructor(readonly entities: IEntitiesStore, private _trigger: ChatbotTrigger) {
    this.enrollmentStore = new EnrollmentStore(
      {
        filterConfig: {
          makeRequest: () =>
            ContactFiltersApi.getFilters({
              withCustomFields: true,
              workflowSource: 'chatbot',
              skipValues: ['tag'],
            }).then(({ data }) => data),
        },
        searchDropdownConfig: {
          includeItemsType: [SearchTypesEnum.tags, SearchTypesEnum.contacts],
          placeholder: 'Search tag or contact',
        },
        excludeSearchDropdownConfig: {
          includeItemsType: [SearchTypesEnum.tags, SearchTypesEnum.contacts],
          placeholder: 'Search tag or contact',
        },
      },
      this.entities
    )
    this.enrollByWebhookStore = new EnrollByWebhookStore()
    this.sendFromStore = new SendFromStore(this.entities, {
      includeRCS: true,
      withDynamicSender: true,
    })

    this.initFilters()
    makeAutoObservable(this)

    this.enrollmentStore.setIsContactReEnroll(this._trigger.allowReEntering)
    this.reactionIsShowContactsAlert()
  }

  reactionIsShowContactsAlert = () => {
    this._disposeIsShowContactsAlert?.()
    this._disposeIsShowContactsAlert = reaction(
      () => [this.enrollmentStore.isContactReEnroll, this.enrollmentFilters.filters],
      () => this.enrollmentStore.setContactReEnrollAlert(this.enrollmentFilters.filters)
    )
  }

  get paramsFilters(): IChatbotFilters {
    return {
      contacts: this.enrollmentFilters.contacts.map((c) => c.id),
      segments: this.enrollmentFilters.segments.map((s) => s.id),
      tags: this.enrollmentFilters.tags.map((t) => t.id),
      advanced: this.enrollmentFilters.filters.map((f) => f.filtersParams),
    }
  }

  get paramsExcludeFilters(): IChatbotFilters {
    return {
      contacts: this.enrollmentExcludeFilters.contacts.map((c) => c.id),
      segments: this.enrollmentExcludeFilters.segments.map((s) => s.id),
      tags: this.enrollmentExcludeFilters.tags.map((t) => t.id),
      advanced: this.enrollmentExcludeFilters.filters.map((f) => f.filtersParams),
    }
  }

  initFilters = () => this.enrollmentStore.initFilters()

  loadFromData = async (data: IChatbotFullData) => {
    this.enrollByWebhookStore.init({
      isActive: !!data.enroll_by_webhook,
      webhook_url: data.webhook_url || undefined,
    })
    const hasFilters = !!(
      data.filters?.contacts?.length ||
      data.filters?.tags?.length ||
      data.filters?.advanced?.length ||
      data.exclude_filters?.contacts?.length ||
      data.exclude_filters?.tags?.length ||
      data.exclude_filters?.advanced?.length
    )

    await Promise.all([this.sendFromStore.init(), this.init(data.filters, data.exclude_filters)])
    await this.setSendFrom(data.send_from)

    this.setRunAutomatically(hasFilters)
    this.setAllowReEntering(this._trigger.allowReEntering)
  }

  setSendFrom = async (params: IParamsSendFrom | null) => {
    await this.sendFromStore.setSendFrom(params)
  }

  setSendFromError = (error: string) => {
    this.sendFromError = error
  }

  clearSendFromError = () => {
    this.sendFromError = ''
  }

  setRunAutomatically = (value: boolean) => {
    this.runAutomatically = value
    if (!value) {
      this.enrollmentFilters = EnrollmentFilters.Empty()
      this.enrollmentExcludeFilters = EnrollmentFilters.Empty()
      this.enrollmentError = ''
      this.setAllowReEntering(false)
    }
  }

  validateEnrollment = () => {
    if (
      this.runAutomatically &&
      !this.enrollmentFilters.allSize &&
      !this.enrollmentExcludeFilters.allSize
    ) {
      this.enrollmentError = 'Select enrollment criteria'
      return false
    }
    return true
  }

  setAllowReEntering = (value: boolean) => {
    this._trigger.setAllowReEntering(value)
    this.enrollmentStore.setIsContactReEnroll(value)
  }

  get allowReEntering() {
    return this._trigger.allowReEntering
  }

  get isShowContactsAlert() {
    return this.enrollmentStore.isShowContactsAlert
  }

  onCloseContactsAlert = () => {
    this.enrollmentStore.setIsShowContactsAlert(false)
  }

  get sendFrom(): IParamsSendFrom | null {
    return this.sendFromStore.paramsSendFrom
  }

  editEnrollment = () => {
    const filters = this.enrollmentFilters.clone()
    const excludeFilters = this.enrollmentExcludeFilters.clone()

    const cancelFilters = () => Promise.resolve(true)

    const confirmFilters: IEnrollmentActions['confirm'] = async ({ filters, excludeFilters }) => {
      runInAction(() => {
        this.enrollmentFilters = filters
        this.enrollmentExcludeFilters = excludeFilters
        if (filters.allSize) this.enrollmentError = ''
      })
      return true
    }

    this.enrollmentStore.openModal({
      title: 'Enroll contacts',
      allowEmptyCount: true,
      filters: filters,
      excludeFilters: excludeFilters,
      action: {
        cancel: cancelFilters,
        confirm: confirmFilters,
      },
    })
  }

  init = async (filters: IChatbotFilters | null, exclude_filters: IChatbotFilters | null) => {
    const [filtersData, excludeFiltersData] = await Promise.all([
      initEnrollmentFilters(this.entities, filters),
      initEnrollmentFilters(this.entities, exclude_filters),
    ])

    if (filtersData) {
      this.enrollmentFilters.sync(filtersData)
    }

    if (excludeFiltersData) {
      this.enrollmentExcludeFilters.sync(excludeFiltersData)
    }
  }

  dispose = () => {
    this._disposeIsShowContactsAlert?.()
  }
}
